<script>
  import { onMount } from 'svelte';
  import { getDiasporaReach, getGlassCeiling, getHealth, getProgrammingPayGap } from './lib/api.js';

  let status = 'checking';
  let indicator = null;
  let indicatorError = '';
  let payGap = null;
  let payGapError = '';
  let diaspora = null;
  let diasporaError = '';

  onMount(() => {
    const controller = new AbortController();
    getHealth({ signal: controller.signal })
      .then(() => { status = 'ready'; })
      .catch((error) => {
        if (error.name !== 'AbortError') status = 'unavailable';
      });
    getGlassCeiling({ signal: controller.signal })
      .then((result) => { indicator = result; })
      .catch((error) => {
        if (error.name !== 'AbortError') indicatorError = error.message;
      });
    getProgrammingPayGap({ signal: controller.signal })
      .then((result) => { payGap = result; })
      .catch((error) => {
        if (error.name !== 'AbortError') payGapError = error.message;
      });
    getDiasporaReach({ signal: controller.signal })
      .then((result) => { diaspora = result; })
      .catch((error) => {
        if (error.name !== 'AbortError') diasporaError = error.message;
      });
    return () => controller.abort();
  });

  const bucketLabels = {
    'all cisgender female': 'All cisgender female',
    'all cisgender male': 'All cisgender male',
    'cisgender mixed': 'Cisgender mixed',
    'includes gender-diverse': 'Includes gender-diverse',
    'includes non-disclosure': 'Includes non-disclosure',
  };

  const formatEuros = (value) => value === null
    ? '—'
    : new Intl.NumberFormat('en', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 }).format(value);

  const regionLabels = {
    lusophone: 'Lusophone',
    iberian: 'Iberian',
    european: 'European',
    other: 'Other',
  };
</script>

<svelte:head><title>CTL Diversity Dashboard</title></svelte:head>

<header class="masthead">
  <a class="brand" href="/" aria-label="CTL Diversity Dashboard home">
    <strong>CTL Diversity Dashboard</strong>
  </a>
  <span class:ready={status === 'ready'} class:error={status === 'unavailable'} class="status">
    <span aria-hidden="true"></span>{status === 'ready' ? 'Data service ready' : status === 'unavailable' ? 'Data service unavailable' : 'Checking data service'}
  </span>
</header>

<main>
  <section class="intro">
    <h1>Diversity indicators</h1>
    <p>Results calculated from the local synthetic dataset.</p>
  </section>

  {#if indicatorError}
    <p class="notice error-notice">Could not load the Glass Ceiling Test: {indicatorError}</p>
  {:else if !indicator}
    <p class="notice">Calculating Glass Ceiling Test…</p>
  {:else}
    <article class="indicator">
      <header class="indicator-heading">
        <div>
          <p class="eyebrow">Indicator 03</p>
          <h2>{indicator.title}</h2>
          <p>{indicator.question}</p>
        </div>
        <dl class="coverage-summary">
          <div><dt>Eligible bookings</dt><dd>{indicator.eligiblePopulation.sampleSize} / {indicator.eligiblePopulation.totalRecords}</dd></div>
          <div><dt>Coverage</dt><dd>{indicator.coverage.percent}%</dd></div>
        </dl>
      </header>

      <section class="headline" aria-labelledby="main-room-title">
        <div>
          <h3 id="main-room-title">Main room (R/C)</h3>
          <p class="result">{indicator.headline.percentagePointDifference > 0 ? '+' : ''}{indicator.headline.percentagePointDifference} percentage points</p>
          <p>All-cisgender-female minus all-cisgender-male room assignments.</p>
          <p><strong>{indicator.headline.interpretation}</strong></p>
        </div>
        <dl>
          <div><dt>All cisgender female</dt><dd>{indicator.headline.allCisgenderFemaleMainRoomPercent}%</dd></div>
          <div><dt>All cisgender male</dt><dd>{indicator.headline.allCisgenderMaleMainRoomPercent}%</dd></div>
          <div><dt>Average capacity difference</dt><dd>{indicator.headline.averageCapacityDifference > 0 ? '+' : ''}{indicator.headline.averageCapacityDifference}</dd></div>
        </dl>
      </section>

      <details class="analysis-details">
        <summary>Room comparison and methodology</summary>

        <section aria-labelledby="distribution-title">
          <h3 id="distribution-title">Room distribution</h3>
          <div class="chart" role="img" aria-label="Room assignment percentage by gender bucket and room">
            {#each indicator.comparison as row}
              <div class="chart-row">
                <strong>{bucketLabels[row.bucket]}</strong>
                <div class="bars">
                  {#each row.spaces as room}
                    <div class="bar-line">
                      <span>{room.space}</span>
                      <div class="track"><i style={`width: ${room.percent ?? 0}%`}></i></div>
                      <span>{room.percent ?? '—'}%</span>
                    </div>
                  {/each}
                </div>
              </div>
            {/each}
          </div>
        </section>

        <section aria-labelledby="table-title">
          <h3 id="table-title">Underlying comparison</h3>
          <div class="table-scroll">
            <table>
              <thead><tr><th>Gender bucket</th><th>Bookings</th><th>Room assignments</th><th>Average capacity</th>{#each indicator.comparison[0].spaces as room}<th>{room.space}</th>{/each}</tr></thead>
              <tbody>
                {#each indicator.comparison as row}
                  <tr><th>{bucketLabels[row.bucket]}</th><td>{row.eventCount}</td><td>{row.roomAssignmentCount}</td><td>{row.averageCapacity ?? '—'}</td>{#each row.spaces as room}<td>{room.eventCount} ({room.percent ?? '—'}%)</td>{/each}</tr>
                {/each}
              </tbody>
            </table>
          </div>
        </section>

        <section class="method-grid">
          <div>
            <h3>Method and coverage</h3>
            <p>{indicator.eligiblePopulation.description}</p>
            <p><strong>Formula v{indicator.formula.version}:</strong> {indicator.formula.expression}.</p>
            <p>{indicator.exclusions.total} bookings excluded. {indicator.coverage.multiRoomEvents} bookings use more than one room.</p>
            {#if indicator.exclusions.byReason.length}
              <ul>{#each indicator.exclusions.byReason as item}<li>{item.count} — {item.reason}</li>{/each}</ul>
            {/if}
          </div>
          <div>
            <h3>Caveats</h3>
            <ul>{#each indicator.caveats as caveat}<li>{caveat}</li>{/each}</ul>
          </div>
        </section>
      </details>

      <details>
        <summary>Eligible source rows ({indicator.eligibleRows.length})</summary>
        <div class="table-scroll source-rows">
          <table>
            <thead><tr><th>Casa row</th><th>Date</th><th>Event</th><th>Gender bucket</th><th>Room</th><th>Notion rows</th></tr></thead>
            <tbody>{#each indicator.eligibleRows as row}<tr><td>{row.sourceRowId}</td><td>{row.eventDate}</td><td>{row.eventName}</td><td>{bucketLabels[row.genderBucket]}</td><td>{row.spaces.join(', ')}</td><td>{row.artistSourceRowIds.join(', ')}</td></tr>{/each}</tbody>
          </table>
        </div>
      </details>
    </article>
  {/if}

  {#if payGapError}
    <p class="notice error-notice">Could not load the Programming Pay Gap: {payGapError}</p>
  {:else if !payGap}
    <p class="notice">Calculating Programming Pay Gap…</p>
  {:else}
    <article class="indicator">
      <header class="indicator-heading">
        <div>
          <p class="eyebrow">Indicator 04</p>
          <h2>{payGap.title}</h2>
          <p>{payGap.question}</p>
        </div>
        <dl class="coverage-summary">
          <div><dt>Eligible bookings</dt><dd>{payGap.eligiblePopulation.dealSampleSize} / {payGap.eligiblePopulation.totalRecords}</dd></div>
          <div><dt>Deal coverage</dt><dd>{payGap.coverage.dealPercent}%</dd></div>
        </dl>
      </header>

      <section class="headline pay-headline" aria-labelledby="pay-gap-title">
        <div>
          <h3 id="pay-gap-title">Guaranteed-fee deal share</h3>
          <p class="result">{payGap.headline.guaranteedFeePercentagePointDifference > 0 ? '+' : ''}{payGap.headline.guaranteedFeePercentagePointDifference} percentage points</p>
          <p>All-cisgender-female minus all-cisgender-male bookings on INV.</p>
          <p><strong>{payGap.headline.dealInterpretation}</strong></p>
        </div>
        <dl>
          <div><dt>All cisgender female</dt><dd>{payGap.headline.allCisgenderFemaleGuaranteedFeePercent}%</dd></div>
          <div><dt>All cisgender male</dt><dd>{payGap.headline.allCisgenderMaleGuaranteedFeePercent}%</dd></div>
        </dl>
        <div>
          <h3>Median recorded fee</h3>
          <p class="result">{payGap.headline.medianFeeDifferenceEuros > 0 ? '+' : ''}{formatEuros(payGap.headline.medianFeeDifferenceEuros)}</p>
          <p>Female minus male single-INV booking median.</p>
          <p><strong>{payGap.headline.feeInterpretation}</strong></p>
        </div>
        <dl>
          <div><dt>All cisgender female</dt><dd>{formatEuros(payGap.headline.allCisgenderFemaleMedianFeeEuros)}</dd></div>
          <div><dt>All cisgender male</dt><dd>{formatEuros(payGap.headline.allCisgenderMaleMedianFeeEuros)}</dd></div>
        </dl>
      </section>

      <details class="analysis-details">
        <summary>Deal and fee comparisons and methodology</summary>

        <section aria-labelledby="deal-table-title">
          <h3 id="deal-table-title">Deal structure distribution</h3>
          <div class="table-scroll">
            <table>
              <thead><tr><th>Gender bucket</th><th>Bookings</th>{#each payGap.comparison.dealStructures[0].deals as deal}<th>{deal.deal}</th>{/each}</tr></thead>
              <tbody>{#each payGap.comparison.dealStructures as row}<tr><th>{bucketLabels[row.bucket]}</th><td>{row.bookingCount}</td>{#each row.deals as deal}<td>{deal.bookingCount} ({deal.percent ?? '—'}%)</td>{/each}</tr>{/each}</tbody>
            </table>
          </div>
        </section>

        <section aria-labelledby="fee-table-title">
          <h3 id="fee-table-title">Recorded fees for single-INV bookings</h3>
          <div class="table-scroll">
            <table>
              <thead><tr><th>Gender bucket</th><th>Bookings</th><th>Median</th><th>Mean</th><th>Range</th><th>Multi-artist bills</th></tr></thead>
              <tbody>{#each payGap.comparison.guaranteedFees as row}<tr><th>{bucketLabels[row.bucket]}</th><td>{row.bookingCount}</td><td>{formatEuros(row.medianFeeEuros)}</td><td>{formatEuros(row.meanFeeEuros)}</td><td>{formatEuros(row.minimumFeeEuros)}–{formatEuros(row.maximumFeeEuros)}</td><td>{row.multiArtistBookingCount}</td></tr>{/each}</tbody>
            </table>
          </div>
        </section>

        <section class="method-grid">
          <div>
            <h3>Method and coverage</h3>
            <p>{payGap.eligiblePopulation.description}</p>
            <p><strong>Formula v{payGap.formula.version}:</strong> {payGap.formula.dealExpression}. {payGap.formula.feeExpression}.</p>
            <p>{payGap.coverage.feeEligibleRecords} of {payGap.eligiblePopulation.guaranteedFeeBookings} eligible INV bookings have a fee ({payGap.coverage.feePercentOfGuaranteedFeeBookings}%). {payGap.coverage.multiArtistFeeBookings} fee records are multi-artist bills.</p>
            {#if payGap.exclusions.dealStructures.byReason.length}
              <ul>{#each payGap.exclusions.dealStructures.byReason as item}<li>{item.count} — {item.reason}</li>{/each}</ul>
            {/if}
          </div>
          <div>
            <h3>Caveats</h3>
            <ul>{#each payGap.caveats as caveat}<li>{caveat}</li>{/each}</ul>
          </div>
        </section>
      </details>

      <details>
        <summary>Eligible source rows ({payGap.eligibleRows.length})</summary>
        <div class="table-scroll source-rows">
          <table>
            <thead><tr><th>Casa row</th><th>Date</th><th>Event</th><th>Gender bucket</th><th>Deal</th><th>Base fee</th><th>Bill</th><th>Notion rows</th></tr></thead>
            <tbody>{#each payGap.eligibleRows as row}<tr><td>{row.sourceRowId}</td><td>{row.eventDate}</td><td>{row.eventName}</td><td>{bucketLabels[row.genderBucket]}</td><td>{row.deals.join(' + ')}</td><td>{row.feeEligible ? formatEuros(row.baseFeeEuros) : '—'}</td><td>{row.multiArtist ? 'Multi-artist' : 'Single artist'}</td><td>{row.artistSourceRowIds.join(', ')}</td></tr>{/each}</tbody>
          </table>
        </div>
      </details>
    </article>
  {/if}

  {#if diasporaError}
    <p class="notice error-notice">Could not load the Diaspora Reach Index: {diasporaError}</p>
  {:else if !diaspora}
    <p class="notice">Calculating Diaspora Reach Index…</p>
  {:else}
    <article class="indicator">
      <header class="indicator-heading">
        <div>
          <p class="eyebrow">Indicator 02</p>
          <h2>{diaspora.title}</h2>
          <p>{diaspora.question}</p>
        </div>
        <dl class="coverage-summary">
          <div><dt>Eligible DICE events</dt><dd>{diaspora.eligiblePopulation.sampleSize} / {diaspora.eligiblePopulation.totalRecords}</dd></div>
          <div><dt>Coverage</dt><dd>{diaspora.coverage.percent}%</dd></div>
        </dl>
      </header>

      <section class="diaspora-headline" aria-labelledby="diaspora-title">
        <h3 id="diaspora-title">Regional reach versus Portuguese-origin events</h3>
        <div class="region-results">
          {#each diaspora.comparison as row}
            <div>
              <strong>{regionLabels[row.region]}</strong>
              <p class="result">{row.reachPercentagePointDifference === null ? '—' : `${row.reachPercentagePointDifference > 0 ? '+' : ''}${row.reachPercentagePointDifference} pp`}</p>
              <span>{row.diasporaEventCount} diaspora events</span>
            </div>
          {/each}
        </div>
      </section>

      <details class="analysis-details">
        <summary>Regional comparison and methodology</summary>
        <section aria-labelledby="diaspora-table-title">
          <h3 id="diaspora-table-title">Underlying comparison</h3>
          <div class="table-scroll">
            <table>
              <thead><tr><th>Artist-origin region</th><th>Single-region events</th><th>Audience from region</th><th>Portuguese baseline events</th><th>Baseline audience from region</th><th>Reach difference</th><th>Including multi-origin events</th></tr></thead>
              <tbody>{#each diaspora.comparison as row}<tr><th>{regionLabels[row.region]}</th><td>{row.diasporaEventCount}</td><td>{row.diasporaAverageAudiencePercent ?? '—'}%</td><td>{row.portugueseBaselineEventCount}</td><td>{row.portugueseBaselineAverageAudiencePercent ?? '—'}%</td><td>{row.reachPercentagePointDifference === null ? '—' : `${row.reachPercentagePointDifference > 0 ? '+' : ''}${row.reachPercentagePointDifference} pp`}</td><td>{row.inclusiveAverageAudiencePercent ?? '—'}% ({row.inclusiveEventCount}); {row.inclusiveReachPercentagePointDifference === null ? '—' : `${row.inclusiveReachPercentagePointDifference > 0 ? '+' : ''}${row.inclusiveReachPercentagePointDifference} pp`}</td></tr>{/each}</tbody>
            </table>
          </div>
        </section>
        <section class="method-grid">
          <div>
            <h3>Method and coverage</h3>
            <p>{diaspora.eligiblePopulation.description}</p>
            <p><strong>Formula v{diaspora.formula.version}:</strong> {diaspora.formula.expression}.</p>
            <p>{diaspora.coverage.portugueseBaselineEvents} Portuguese-only baseline events; {diaspora.coverage.multiOriginEvents} eligible events have multiple origin regions.</p>
            {#if diaspora.exclusions.byReason.length}<ul>{#each diaspora.exclusions.byReason as item}<li>{item.count} — {item.reason}</li>{/each}</ul>{/if}
          </div>
          <div>
            <h3>Caveats</h3>
            <ul>{#each diaspora.caveats as caveat}<li>{caveat}</li>{/each}</ul>
          </div>
        </section>
      </details>

      <details>
        <summary>Eligible source rows ({diaspora.eligibleRows.length})</summary>
        <div class="table-scroll source-rows">
          <table>
            <thead><tr><th>DICE row</th><th>Date</th><th>Event</th><th>Artist countries</th><th>Origin regions</th><th>Audience</th><th>Regional audience shares</th><th>Notion rows</th></tr></thead>
            <tbody>{#each diaspora.eligibleRows as row}<tr><td>{row.sourceRowId}</td><td>{row.eventDate}</td><td>{row.eventName}</td><td>{row.artistCountries.join(', ')}</td><td>{row.originRegions.map((region) => regionLabels[region] ?? 'Portuguese baseline').join(', ')}</td><td>{row.audienceTotal}</td><td>{Object.entries(row.regionAudiencePercentages).map(([region, value]) => `${regionLabels[region]} ${value}%`).join('; ')}</td><td>{row.artistSourceRowIds.join(', ')}</td></tr>{/each}</tbody>
          </table>
        </div>
      </details>
    </article>
  {/if}
</main>

<footer>Synthetic development data · SQLite backend</footer>
